import {
  ArrowLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  PlusIcon,
} from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  FormDefinition,
  FormFieldCatalog,
  FormFieldConfig,
  FormFieldInfo,
  FormQuestion,
  FormSetValue,
  FormsService,
  OrganizationResponseForAdmins,
  OrganizationsService,
  PublicFieldView,
  PublicFormView,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { FormPaymentPlanFields } from "components/Forms/DefaultPaymentPlanSetting";
import FieldValueInput, { FieldValue } from "components/FieldValueInput";
import { errorText } from "components/Forms/formsApi";
import InquiryLinks from "components/Forms/InquiryLinks";
import { PaymentPreview } from "components/Forms/PaymentStep";
import PublicForm from "components/Forms/PublicForm";
import {
  Grip,
  SortableList,
  lineClass,
  moved,
  useSortableItem,
} from "components/Forms/SortableList";
import { useAutosave } from "components/Forms/useAutosave";
import { Select } from "components/Select";
import { Switch } from "components/Switch";
import { OrgAdminShell } from "pages/org-performance/OrgAdminPage";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import Notifications from "util/notifications";
import { useOrganizationRenamed } from "util/organizationRenamed";

type PersonSectionKey = "parent" | "student" | "tutor";
type Audience = "families" | "tutors" | "inquiry";

const AUDIENCE_NAMES: Record<Audience, string> = {
  families: "Families",
  tutors: "Tutors",
  inquiry: "Website inquiry",
};

/** Who a form is for, and the people of each audience (the server's rule). */
const AUDIENCE_PEOPLE: Record<Audience, PersonSectionKey[]> = {
  families: ["parent", "student"],
  tutors: ["tutor"],
  inquiry: ["parent", "student"],
};

/** Whose page the preview can show: an inquiry is filled out by a parent. */
const PREVIEW_PEOPLE: Record<Audience, PersonSectionKey[]> = {
  ...AUDIENCE_PEOPLE,
  inquiry: ["parent"],
};

function audienceOf(definition: FormDefinition): Audience {
  return definition.audience === "tutors" || definition.audience === "inquiry"
    ? definition.audience
    : "families";
}

/** The person the preview shows: the one picked, if the audience has them. */
function previewPerson(
  definition: FormDefinition,
  picked: PersonSectionKey
): PersonSectionKey {
  const people = PREVIEW_PEOPLE[audienceOf(definition)];
  return people.includes(picked) ? picked : people[0];
}

const NAMES = ["first_name", "last_name"];

/**
 * The server's rule for a website inquiry: the parent section is on and
 * asks for names (so do students), nothing on file is shown, no payment.
 * Applied only to what the builder shows; the saved settings stay the
 * admin's, so switching back finds them as they were.
 */
function asShown(definition: FormDefinition): FormDefinition {
  return audienceOf(definition) === "inquiry"
    ? { ...definition, sections: keptToInquiry(definition.sections || {}) }
    : definition;
}

function keptToInquiry(
  sections: NonNullable<FormDefinition["sections"]>
): NonNullable<FormDefinition["sections"]> {
  const people = (section: "parent" | "student") => {
    const fields = sections[section]?.fields || [];
    const missing = NAMES.filter((n) => !fields.some((c) => c.field === n));
    return [
      ...missing.map((field) => ({ field, collect: true, required: true })),
      ...fields.map((c) =>
        NAMES.includes(c.field) ? { ...c, collect: true, required: true } : c
      ),
    ].map((c) => ({ ...c, show_on_file: false }));
  };
  return {
    ...sections,
    parent: { ...sections.parent, enabled: true, fields: people("parent") },
    student: { ...sections.student, fields: people("student") },
    payment: { ...sections.payment, enabled: false },
    tutor: { ...sections.tutor, enabled: false, payroll_bank: false },
  };
}

const SECTION_COPY: Record<PersonSectionKey, { title: string; note: string }> =
  {
    parent: {
      title: "Parent",
      note: "Questions for the parent.",
    },
    student: {
      title: "Students",
      note: "Questions about each student.",
    },
    tutor: {
      title: "Tutor",
      note: "Questions for the tutor.",
    },
  };

function configFor(
  definition: FormDefinition,
  section: PersonSectionKey,
  field: string
): FormFieldConfig {
  return (
    definition.sections?.[section]?.fields?.find((c) => c.field === field) || {
      field,
      collect: false,
      required: false,
      show_on_file: true,
    }
  );
}

/**
 * A section's fields in the form's order: the ones it has saved, in their
 * order, and each other field right after the one it follows in the
 * catalog. A form never reordered keeps the catalog's order, and a new CRM
 * field shows up where it always would have.
 */
function orderedFields(
  definition: FormDefinition,
  fields: FormFieldInfo[],
  section: PersonSectionKey
): FormFieldInfo[] {
  const byField = new Map(fields.map((info) => [info.field, info]));
  const order: string[] = [];
  (definition.sections?.[section]?.fields ?? []).forEach(({ field }) => {
    if (byField.has(field) && !order.includes(field)) order.push(field);
  });
  fields.forEach((info, index) => {
    if (order.includes(info.field)) return;
    const after = index === 0 ? -1 : order.indexOf(fields[index - 1].field);
    order.splice(after + 1, 0, info.field);
  });
  return order.map((field) => byField.get(field) as FormFieldInfo);
}

function asked(
  definition: FormDefinition,
  catalog: FormFieldCatalog,
  section: PersonSectionKey
): PublicFieldView[] {
  const settings = definition.sections?.[section];
  if (!settings?.enabled) return [];
  return orderedFields(definition, catalog[section], section)
    .map((info) => ({
      info,
      config: configFor(definition, section, info.field),
    }))
    .filter(({ config }) => config.collect)
    .map(
      ({ info, config }): PublicFieldView => ({
        field: info.field,
        label: info.label,
        input: info.input,
        required: !!config.required,
        prompt: config.prompt?.trim() || null,
        options: info.options,
      })
    )
    .concat(
      // Note questions follow the fields, as the server asks them.
      ("questions" in settings ? settings.questions ?? [] : [])
        .filter((question) => question.label?.trim())
        .map(
          (question): PublicFieldView => ({
            field: `note:${question.id}`,
            label: (question.label as string).trim(),
            input: "textarea",
            required: !!question.required,
            prompt: question.prompt?.trim() || null,
          })
        )
    );
}

/** A note question's id: 12 hex characters, made once, never changed. */
function newQuestionId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(6)), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
}

/**
 * The chevron that opens a field's or question's prompt: directions or
 * context the form shows under it.
 */
function PromptToggle({
  open,
  onClick,
  name,
}: {
  open: boolean;
  onClick: () => void;
  name: string;
}) {
  return (
    <button
      type="button"
      className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
      aria-expanded={open}
      aria-label={`Prompt for ${name}`}
      title={open ? "Hide the prompt" : "Add a prompt"}
      onClick={onClick}
    >
      <ChevronRightIcon
        className={`transition-transform duration-150 ${
          open ? "rotate-90" : ""
        }`}
      />
    </button>
  );
}

/** A prompt's text box, under its field or question. */
function PromptInput({
  id,
  value,
  onChange,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (prompt: string) => void;
  autoFocus: boolean;
}) {
  return (
    <>
      <label
        htmlFor={id}
        className="block text-xs font-semibold text-gray-600 mb-1"
      >
        Prompt
        <span className="font-normal text-gray-500"> (optional)</span>
      </label>
      <textarea
        id={id}
        className="input w-full text-sm"
        rows={2}
        maxLength={500}
        placeholder="Directions or more context, shown under the question"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </>
  );
}

/** Whether a prompt is open: open from the start when there is one. */
function usePromptOpen(prompt?: string) {
  const [open, setOpen] = useState(!!prompt?.trim());
  // Opened by a click (not on load): the text box takes the cursor.
  const [clicked, setClicked] = useState(false);
  return {
    open,
    clicked,
    toggle: () => {
      setClicked(!open);
      setOpen(!open);
    },
  };
}

/** One note question: dragged by its grip, with Required and its prompt. */
function QuestionRow({
  question,
  index,
  autoFocus,
  onChange,
  onRemove,
}: {
  question: FormQuestion;
  index: number;
  autoFocus: boolean;
  onChange: (patch: Partial<FormQuestion>) => void;
  onRemove: () => void;
}) {
  const item = useSortableItem(question.id);
  const prompt = usePromptOpen(question.prompt);
  const name = `question ${index + 1}`;
  return (
    <div
      ref={item.rowRef}
      className={`rounded-lg py-1 ${
        item.isDragging ? "opacity-40" : ""
      } ${lineClass(item.line)}`}
    >
      <div className="flex items-center gap-1">
        <Grip label={`Drag ${name} to reorder`} item={item} />
        <PromptToggle open={prompt.open} onClick={prompt.toggle} name={name} />
        <input
          className="input ml-1 w-full"
          aria-label={`Question ${index + 1}`}
          placeholder="Ask anything"
          maxLength={200}
          autoFocus={autoFocus}
          value={question.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <div className="ml-2 shrink-0">
          <Switch
            name={`question-${question.id}-required`}
            label={`Question ${index + 1} is required`}
            checked={!!question.required}
            onCheckedChange={(on) => onChange({ required: on })}
          >
            <span className="mr-2 text-sm text-gray-600">Required</span>
          </Switch>
        </div>
        <button
          type="button"
          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label={`Remove question ${index + 1}`}
          title="Remove"
          onClick={onRemove}
        >
          <Cross2Icon />
        </button>
      </div>
      {prompt.open && (
        <div className="mt-2 mb-1 pl-[3.625rem] pr-8">
          <PromptInput
            id={`question-${question.id}-prompt`}
            value={question.prompt ?? ""}
            onChange={(text) => onChange({ prompt: text })}
            autoFocus={prompt.clicked}
          />
        </div>
      )}
    </div>
  );
}

/**
 * A section's note questions: each a text box the family answers. The
 * answer is kept on the response and added to the person's notes.
 */
function Questions({
  questions,
  onChange,
}: {
  questions: FormQuestion[];
  onChange: (questions: FormQuestion[]) => void;
}) {
  // The question just added gets the cursor.
  const added = useRef<string>();
  return (
    <div className="mt-5 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-900">Questions</h4>
      <p className="mt-0.5 text-sm text-gray-600">
        Answers are saved as a note.
      </p>
      {questions.length > 0 && (
        <SortableList
          ids={questions.map((question) => question.id)}
          onMove={(from, to) => onChange(moved(questions, from, to))}
          overlay={(id) =>
            questions.find((question) => question.id === id)?.label?.trim() ||
            "Question"
          }
        >
          <div className="mt-3 space-y-1">
            {questions.map((question, index) => (
              <QuestionRow
                key={question.id}
                question={question}
                index={index}
                autoFocus={added.current === question.id}
                onChange={(patch) =>
                  onChange(
                    questions.map((other) =>
                      other.id === question.id ? { ...other, ...patch } : other
                    )
                  )
                }
                onRemove={() =>
                  onChange(
                    questions.filter((other) => other.id !== question.id)
                  )
                }
              />
            ))}
          </div>
        </SortableList>
      )}
      <Button
        size={ButtonSize.SMALL}
        fill={ButtonFill.HOLLOW}
        color={ButtonColor.GREEN}
        extraClasses="mt-3"
        onClick={() => {
          const id = newQuestionId();
          added.current = id;
          // A new question starts optional.
          onChange([...questions, { id, label: "", required: false }]);
        }}
      >
        <PlusIcon /> Add a question
      </Button>
    </div>
  );
}

/** The family's page for a sample person, built the same way the server builds it. */
function previewView(
  definition: FormDefinition,
  catalog: FormFieldCatalog,
  as: PersonSectionKey,
  formName: string,
  orgName: string,
  takesPayments: boolean
): PublicFormView {
  const view: PublicFormView = {
    state: PublicFormView.state.OPEN,
    org_name: orgName,
    form_name: formName,
    intro: definition.intro || null,
    revision: 0,
    allow_add_students: false,
  };
  const studentFields = asked(definition, catalog, "student");
  const nameFields: PublicFieldView[] = [
    { field: "first_name", label: "First name", input: "text", required: true },
    { field: "last_name", label: "Last name", input: "text", required: true },
  ];
  // The server's rule: names are always asked and required, where the
  // form asks them; a name it doesn't ask sits beside the other (first, then
  // last), or first when it asks neither.
  const newStudentFields: PublicFieldView[] = studentFields.map((f) =>
    NAMES.includes(f.field) ? { ...f, required: true } : f
  );
  nameFields.forEach((name) => {
    if (newStudentFields.some((f) => f.field === name.field)) return;
    const at =
      name.field === "last_name"
        ? newStudentFields.findIndex((f) => f.field === "first_name") + 1
        : Math.max(
            0,
            newStudentFields.findIndex((f) => f.field === "last_name")
          );
    newStudentFields.splice(at, 0, name);
  });
  if (audienceOf(definition) === "inquiry") {
    // A new family: nobody on file, and every student is one they add.
    view.recipient_type = Auth0AccountRole.PARENT;
    view.parent_fields = asked(definition, catalog, "parent");
    if (definition.sections?.student?.enabled) {
      view.student_blocks = [];
      view.allow_add_students = true;
      view.new_student_fields = newStudentFields;
    }
  } else if (as === "parent") {
    view.recipient_type = Auth0AccountRole.PARENT;
    view.parent_fields = asked(definition, catalog, "parent");
    if (studentFields.length > 0) {
      view.student_blocks = [
        { student_id: -1, name: "Alex Sample", fields: studentFields },
      ];
      if (definition.sections?.student?.allow_add) {
        view.allow_add_students = true;
        view.new_student_fields = newStudentFields;
      }
    }
  } else if (as === "student") {
    view.recipient_type = Auth0AccountRole.ME;
    if (studentFields.length > 0) {
      view.student_blocks = [
        { student_id: -1, name: "Alex Sample", fields: studentFields },
      ];
    }
  } else {
    view.recipient_type = Auth0AccountRole.ORG_TUTOR;
    view.tutor_fields = asked(definition, catalog, "tutor");
    // The server's rule: a tutors form with the payroll bank on.
    if (
      audienceOf(definition) === "tutors" &&
      definition.sections?.tutor?.payroll_bank
    ) {
      view.payroll_bank = { ready: true };
    }
  }
  // Terms with no wording are not shown (the server's rule too).
  if (
    definition.sections?.terms?.enabled &&
    definition.sections.terms.text?.trim()
  ) {
    view.terms_text = definition.sections.terms.text;
  }
  // A families form's parents and students get the payment step.
  if (
    takesPayments &&
    definition.sections?.payment?.enabled &&
    audienceOf(definition) === "families"
  ) {
    view.payment = { required: !!definition.sections.payment.required };
  }
  return view;
}

function Card({
  title,
  note,
  enabled,
  onToggle,
  children,
  lockedReason,
  alwaysOn = false,
  waiting = false,
}: {
  title: string;
  note: string;
  enabled: boolean;
  onToggle: (on: boolean) => void;
  children?: ReactNode;
  lockedReason?: string;
  alwaysOn?: boolean;
  /** Shown as it is, but not changed until what decides the lock arrives. */
  waiting?: boolean;
}) {
  const name = `section-${title}`;
  return (
    <section className="rounded-xl bg-neutral-50 p-5 mt-5">
      <Switch
        name={name}
        checked={(enabled || alwaysOn) && !lockedReason}
        onCheckedChange={onToggle}
        label={`Ask the ${title} section`}
        disabled={!!lockedReason || alwaysOn || waiting}
      >
        <div>
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{note}</p>
        </div>
      </Switch>
      {lockedReason ? (
        <p className="mt-3 text-sm text-gray-500">{lockedReason}</p>
      ) : (
        (enabled || alwaysOn) &&
        children && <div className="mt-4">{children}</div>
      )}
    </section>
  );
}

/** One field of a section's table: its grip, its prompt, and its switches. */
function FieldRow({
  section,
  info,
  definition,
  onChange,
  inquiry,
}: {
  section: PersonSectionKey;
  info: FormFieldInfo;
  definition: FormDefinition;
  onChange: (field: string, patch: Partial<FormFieldConfig>) => void;
  inquiry: boolean;
}) {
  const config = configFor(definition, section, info.field);
  const item = useSortableItem(info.field);
  const prompt = usePromptOpen(config.prompt);
  const id = `${section}-${info.field}`;
  const name = inquiry && NAMES.includes(info.field);
  // Set on submit: never asked too.
  const isSet = !!definition.sections?.[section]?.set?.some(
    (entry) => entry.field === info.field
  );
  return (
    <tbody
      ref={item.rowRef}
      className={`border-b border-gray-100 ${
        item.isDragging ? "opacity-40" : ""
      } ${lineClass(item.line, true)}`}
    >
      <tr>
        <td className="p-3 text-sm text-gray-900">
          <div className="flex items-center gap-1">
            <Grip label={`Drag ${info.label} to reorder`} item={item} />
            <PromptToggle
              open={prompt.open}
              onClick={prompt.toggle}
              name={info.label}
            />
            <span className="ml-1">
              {info.label}
              {info.kind === FormFieldInfo.kind.CRM && (
                <span className="ml-2 text-xs text-gray-500">CRM</span>
              )}
              {isSet && (
                <span className="ml-2 text-xs text-gray-500">
                  Set on submit
                </span>
              )}
            </span>
          </div>
        </td>
        <td className="p-3">
          <div className="flex justify-center">
            <Switch
              name={`${id}-ask`}
              label={`Ask ${info.label}`}
              checked={(!!config.collect || name) && !isSet}
              disabled={name || isSet}
              onCheckedChange={(on) =>
                onChange(
                  info.field,
                  on ? { collect: true } : { collect: false, required: false }
                )
              }
            >
              {null}
            </Switch>
          </div>
        </td>
        <td className="p-3">
          <div className="flex justify-center">
            <Switch
              name={`${id}-required`}
              label={`${info.label} is required`}
              checked={(!!config.required || name) && !isSet}
              disabled={!config.collect || name || isSet}
              onCheckedChange={(on) => onChange(info.field, { required: on })}
            >
              {null}
            </Switch>
          </div>
        </td>
        {!inquiry && (
          <td className="p-3">
            <div className="flex justify-center">
              <Switch
                name={`${id}-show`}
                label={`Show the current ${info.label}`}
                checked={config.show_on_file !== false}
                disabled={!config.collect}
                onCheckedChange={(on) =>
                  onChange(info.field, { show_on_file: on })
                }
              >
                {null}
              </Switch>
            </div>
          </td>
        )}
      </tr>
      {prompt.open && (
        <tr>
          <td colSpan={inquiry ? 3 : 4} className="pb-3 pl-[4.375rem] pr-3">
            <PromptInput
              id={`${id}-prompt`}
              value={config.prompt ?? ""}
              onChange={(text) => onChange(info.field, { prompt: text })}
              autoFocus={prompt.clicked}
            />
          </td>
        </tr>
      )}
    </tbody>
  );
}

function FieldTable({
  section,
  fields,
  definition,
  onChange,
  onMove,
  inquiry = false,
}: {
  section: PersonSectionKey;
  /** The section's fields in the form's order (orderedFields). */
  fields: FormFieldInfo[];
  definition: FormDefinition;
  onChange: (field: string, patch: Partial<FormFieldConfig>) => void;
  /** Move a field to where another one is. */
  onMove: (field: string, onto: string) => void;
  /** A website inquiry: names are always asked, and nothing is on file. */
  inquiry?: boolean;
}) {
  const ids = fields.map((info) => info.field);
  return (
    <SortableList
      ids={ids}
      onMove={(from, to) => onMove(ids[from], ids[to])}
      overlay={(field) =>
        fields.find((info) => info.field === field)?.label ?? field
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse bg-white rounded-lg">
          <thead>
            <tr className="border-b-2 border-gray-300">
              <th className="text-left p-3 text-sm font-bold">Field</th>
              <th className="p-3 text-sm font-bold text-center w-24">Ask</th>
              <th className="p-3 text-sm font-bold text-center w-24">
                Required
              </th>
              {!inquiry && (
                <th className="p-3 text-sm font-bold text-center w-36">
                  Show current answer
                </th>
              )}
            </tr>
          </thead>
          {fields.map((info) => (
            <FieldRow
              key={info.field}
              section={section}
              info={info}
              definition={definition}
              onChange={onChange}
              inquiry={inquiry}
            />
          ))}
        </table>
      </div>
    </SortableList>
  );
}

/**
 * "Set when submitted": CRM fields (and Subjects) a section sets to a
 * fixed value on every submit, never shown to the person filling it out.
 * Starts empty; the picker offers what the section can set and doesn't ask.
 */
function SetValues({
  section,
  fields,
  definition,
  onChange,
}: {
  section: PersonSectionKey;
  fields: FormFieldInfo[];
  definition: FormDefinition;
  onChange: (set: FormSetValue[]) => void;
}) {
  const settings = definition.sections?.[section];
  const set = settings?.set ?? [];
  const asked = new Set(
    (settings?.fields ?? []).filter((c) => c.collect).map((c) => c.field)
  );
  const infos = new Map(fields.map((info) => [info.field, info]));
  const options = fields
    .filter(
      (info) =>
        (info.kind === FormFieldInfo.kind.CRM || info.field === "subjects") &&
        !asked.has(info.field) &&
        !set.some((entry) => entry.field === info.field)
    )
    .map((info) => ({ value: info.field, label: info.label }));

  return (
    <div className="mt-5 border-t border-gray-200 pt-4" data-set-area={section}>
      <h4 className="text-sm font-bold text-gray-900">Set when submitted</h4>
      <p className="text-sm text-gray-600">
        The person filling out the form does not see these.
      </p>
      {set.map((entry) => {
        const info = infos.get(entry.field);
        if (!info) return null;
        return (
          <div
            key={entry.field}
            data-set-row
            className="mt-3 grid grid-cols-[minmax(0,180px)_minmax(0,1fr)_auto] items-center gap-3"
          >
            <span className="text-sm text-gray-900">{info.label}</span>
            <FieldValueInput
              field={{
                field: info.field,
                label: info.label,
                input: info.input,
                required: false,
                options: info.options,
                min_value: info.min_value,
                max_value: info.max_value,
              }}
              value={(entry.value ?? null) as FieldValue}
              onChange={(value) =>
                onChange(
                  set.map((s) =>
                    s.field === entry.field ? { ...s, value } : s
                  )
                )
              }
            />
            <button
              type="button"
              className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
              aria-label={`Stop setting ${info.label}`}
              onClick={() =>
                onChange(set.filter((s) => s.field !== entry.field))
              }
            >
              <Cross2Icon />
            </button>
          </div>
        );
      })}
      <div className="mt-3 max-w-xs">
        <Select
          options={options}
          value={null}
          onChange={(option: any) =>
            option &&
            onChange([
              ...set,
              {
                field: option.value,
                value:
                  infos.get(option.value)?.input === "subjects" ? [] : null,
              },
            ])
          }
          placeholder={options.length ? "Add a field" : "No fields to add"}
          isDisabled={options.length === 0}
        />
      </div>
    </div>
  );
}

function Builder({ orgId }: { orgId: number }) {
  const { formId } = useParams();
  const id = Number(formId);
  const navigate = useNavigate();

  const [catalog, setCatalog] = useState<FormFieldCatalog>();
  const [name, setName] = useState("");
  const [definition, setDefinition] = useState<FormDefinition>();
  const [loadError, setLoadError] = useState<string>();
  const [previewAs, setPreviewAs] = useState<PersonSectionKey>("parent");
  const [publicId, setPublicId] = useState<string | null>();
  const [organization, setOrganization] =
    useState<OrganizationResponseForAdmins>();
  // A payment plan number that isn't allowed: it isn't in the definition.
  const [planInvalid, setPlanInvalid] = useState(false);
  const {
    pending,
    error: saveError,
    save,
    markSaved,
  } = useAutosave(
    definition && { name, definition },
    ({ name: formName, definition: formDefinition }) =>
      FormsService.updateForm({
        formId: id,
        requestBody: {
          name: formName.trim() || undefined,
          definition: formDefinition,
        },
      }).then((form) => setPublicId(form.public_id)),
    800
  );

  const load = useCallback(() => {
    setLoadError(undefined);
    Promise.all([
      FormsService.getForm({ formId: id }),
      FormsService.getFormFieldCatalog({ orgId }),
    ])
      .then(([form, fields]) => {
        markSaved({ name: form.name, definition: form.definition });
        setName(form.name);
        setDefinition(form.definition);
        setPublicId(form.public_id);
        setCatalog(fields);
      })
      .catch((e) =>
        setLoadError(errorText(e, "This form could not be loaded."))
      );
  }, [id, orgId, markSaved]);

  useEffect(load, [load]);

  // Only the preview's name and the payment lock need the whole
  // organization: it loads alongside the form, never before it.
  useEffect(() => {
    OrganizationsService.getOrganization({ orgId })
      .then((org) => setOrganization(org as OrganizationResponseForAdmins))
      .catch((e) =>
        Notifications.error(
          errorText(e, "Your organization could not be loaded.")
        )
      );
  }, [orgId]);
  // The preview follows a rename.
  useOrganizationRenamed((renamed) =>
    setOrganization((org) =>
      org?.id === renamed.id ? { ...org, name: renamed.name } : org
    )
  );
  // Until the organization loads, a space holds its name's line and
  // payments count as set up (the usual case), so nothing moves when it
  // arrives; the payment switches wait for it, so an organization without
  // Stripe can't turn payment on before its card locks.
  const orgName = organization?.name ?? "\u00a0";
  const takesPayments = !organization || !!organization.stripe_account_id;

  /**
   * Done and back: save what is pending, then leave (stay on an error, or
   * while a payment plan number isn't allowed).
   */
  async function leave(to: string) {
    if ((await save()) && !planInvalid) navigate(to);
  }

  function setSection(
    section: "parent" | "student" | "tutor" | "terms" | "payment",
    patch: object
  ) {
    if (!definition) return;
    setDefinition({
      ...definition,
      sections: {
        ...definition.sections,
        [section]: { ...(definition.sections as any)?.[section], ...patch },
      },
    });
  }

  /**
   * Every field of the section in the order shown, so the saved order is
   * the one the admin sees (and the order the form asks them in).
   */
  function savedFields(
    section: PersonSectionKey,
    infos: FormFieldInfo[]
  ): FormFieldConfig[] {
    return infos.map((info) =>
      configFor(definition as FormDefinition, section, info.field)
    );
  }

  function setField(
    section: PersonSectionKey,
    field: string,
    patch: Partial<FormFieldConfig>
  ) {
    if (!definition || !catalog) return;
    const fields = savedFields(
      section,
      orderedFields(definition, catalog[section], section)
    ).map((config) =>
      config.field === field ? { ...config, ...patch } : config
    );
    setSection(section, { fields });
  }

  /** A field dragged onto another one's place, within its section. */
  function moveField(section: PersonSectionKey, field: string, onto: string) {
    if (!definition || !catalog) return;
    const infos = orderedFields(definition, catalog[section], section);
    const from = infos.findIndex((info) => info.field === field);
    const to = infos.findIndex((info) => info.field === onto);
    if (from < 0 || to < 0 || from === to) return;
    setSection(section, {
      fields: savedFields(section, moved(infos, from, to)),
    });
  }

  /**
   * The other audience's sections go off (the server does the same). A
   * website inquiry's own rules are only shown, never saved.
   */
  function setAudience(next: Audience) {
    if (!definition || audienceOf(definition) === next) return;
    const current = definition.sections || {};
    setDefinition({
      ...definition,
      audience: next,
      sections:
        next === "tutors"
          ? {
              ...current,
              parent: { ...current.parent, enabled: false },
              student: { ...current.student, enabled: false },
              payment: { ...current.payment, enabled: false },
            }
          : {
              ...current,
              tutor: { ...current.tutor, enabled: false, payroll_bank: false },
            },
    });
    setPreviewAs(PREVIEW_PEOPLE[next][0]);
  }

  // One preview object per change, so the preview keeps what was typed in it.
  const preview = useMemo(
    () =>
      definition && catalog
        ? previewView(
            asShown(definition),
            catalog,
            previewPerson(definition, previewAs),
            name,
            orgName,
            takesPayments
          )
        : undefined,
    [definition, catalog, previewAs, name, orgName, takesPayments]
  );

  if (loadError) {
    return (
      <div className="py-6 px-7 rounded-xl bg-neutral-50">
        <p className="text-sm text-red-600">{loadError}</p>
        <Button
          color={ButtonColor.SKYBLUE}
          size={ButtonSize.SMALL}
          onClick={load}
          extraClasses="mt-3"
        >
          Try again
        </Button>
      </div>
    );
  }
  if (!definition || !catalog || !preview) {
    return (
      <div className="py-6 px-7 rounded-xl bg-neutral-50 animate-pulse space-y-3">
        <div className="h-8 w-1/3 rounded bg-gray-200" />
        <div className="h-40 rounded bg-gray-100" />
      </div>
    );
  }

  const sections = definition.sections || {};
  const audience = audienceOf(definition);
  const shown = asShown(definition);
  const shownAs = previewPerson(definition, previewAs);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_420px] pb-16">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => leave("/forms")}
            className="flex items-center gap-1 text-sm font-semibold text-gray-700"
          >
            <ArrowLeftIcon /> Forms
          </button>
          <div className="flex items-center gap-3">
            <span
              className={`text-sm ${
                saveError || planInvalid ? "text-red-600" : "text-gray-500"
              }`}
              aria-live="polite"
            >
              {planInvalid
                ? "Not saved"
                : saveError || (pending ? "Saving…" : "Saved")}
            </span>
            <Button
              color={ButtonColor.GREEN}
              size={ButtonSize.SMALL}
              onClick={() => leave(`/forms/${id}`)}
            >
              Done
            </Button>
          </div>
        </div>

        <input
          className="input mt-4 w-full text-2xl font-bold text-gray-900"
          value={name}
          maxLength={80}
          aria-label="Form name"
          placeholder="Form name"
          onChange={(e) => setName(e.target.value)}
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-gray-700">
            Who fills it out
          </span>
          <div
            className="inline-flex rounded-full border border-[#98acfc] p-1"
            role="group"
            aria-label="Who fills it out"
          >
            {(["families", "tutors", "inquiry"] as Audience[]).map((choice) => (
              <button
                key={choice}
                type="button"
                aria-pressed={audience === choice}
                className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${
                  audience === choice
                    ? "bg-[#98acfc] text-white"
                    : "text-[#98acfc] hover:bg-[#98acfc]/10"
                }`}
                onClick={() => setAudience(choice)}
              >
                {AUDIENCE_NAMES[choice]}
              </button>
            ))}
          </div>
        </div>
        {audience === "inquiry" && (
          <div className="mt-3 rounded-lg border border-[#98acfc]/40 bg-white px-4 py-3">
            <p className="text-sm text-gray-700">
              Put this form on your website. Anyone can fill it out, and each
              person who does is added to Tadpole as a new parent, with the
              students they add.
            </p>
            {publicId ? (
              <div className="mt-3">
                <InquiryLinks publicId={publicId} formName={name} />
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">
                The link is ready once this is saved.
              </p>
            )}
          </div>
        )}
        <textarea
          className="input mt-3 w-full"
          rows={2}
          maxLength={1000}
          placeholder="A sentence shown at the top (optional)"
          value={definition.intro || ""}
          onChange={(e) =>
            setDefinition({ ...definition, intro: e.target.value })
          }
        />

        {AUDIENCE_PEOPLE[audience].map((section) => (
          <Card
            key={section}
            title={SECTION_COPY[section].title}
            note={
              audience === "inquiry" && section === "student"
                ? "Questions about each student they add."
                : SECTION_COPY[section].note
            }
            enabled={!!sections[section]?.enabled}
            onToggle={(on) => setSection(section, { enabled: on })}
            alwaysOn={audience === "inquiry" && section === "parent"}
          >
            {section === "student" && audience !== "inquiry" && (
              <div className="mb-4">
                <Switch
                  name="allow-add"
                  checked={!!sections.student?.allow_add}
                  onCheckedChange={(on) =>
                    setSection("student", { allow_add: on })
                  }
                >
                  <span className="text-sm text-gray-800">
                    Let parents add a new student
                  </span>
                </Switch>
              </div>
            )}
            <FieldTable
              section={section}
              fields={orderedFields(definition, catalog[section], section)}
              definition={shown}
              onChange={(field, patch) => setField(section, field, patch)}
              onMove={(field, onto) => moveField(section, field, onto)}
              inquiry={audience === "inquiry"}
            />
            {section !== "tutor" && (
              <Questions
                questions={sections[section]?.questions ?? []}
                onChange={(questions) => setSection(section, { questions })}
              />
            )}
            <SetValues
              section={section}
              fields={catalog[section]}
              definition={shown}
              onChange={(set) => setSection(section, { set })}
            />
          </Card>
        ))}

        <Card
          title="Terms"
          note="People agree to your terms before they submit."
          enabled={!!sections.terms?.enabled}
          onToggle={(on) => setSection("terms", { enabled: on })}
        >
          <textarea
            className="input w-full"
            rows={8}
            maxLength={100000}
            placeholder="Paste your terms and conditions here."
            value={sections.terms?.text || ""}
            onChange={(e) => setSection("terms", { text: e.target.value })}
          />
          {!sections.terms?.text?.trim() && (
            <p className="mt-1 text-sm text-gray-600">
              Terms show once you paste them in.
            </p>
          )}
        </Card>

        {audience === "families" && (
          <Card
            title="Payment"
            note="Families can add or remove a card or bank account. Nothing is charged."
            enabled={!!sections.payment?.enabled}
            onToggle={(on) => setSection("payment", { enabled: on })}
            waiting={!organization}
            lockedReason={
              takesPayments
                ? undefined
                : "Your organization is not set up to take payments through Stripe yet."
            }
          >
            <Switch
              name="payment-required"
              checked={!!sections.payment?.required}
              onCheckedChange={(on) => setSection("payment", { required: on })}
              disabled={!organization}
            >
              <span className="text-sm text-gray-800">
                Require a card or bank account
              </span>
            </Switch>

            <div className="mt-4 rounded-lg bg-white px-4 pt-4 pb-1">
              <FormPaymentPlanFields
                orgId={orgId}
                plan={sections.payment?.plan}
                onChange={(plan) => setSection("payment", { plan })}
                onInvalid={setPlanInvalid}
              />
            </div>
          </Card>
        )}

        {audience === "tutors" && (
          <Card
            title="Payroll bank"
            note="Tutors connect the bank account their pay goes to."
            enabled={!!sections.tutor?.payroll_bank}
            onToggle={(on) => setSection("tutor", { payroll_bank: on })}
          />
        )}
      </div>

      <div className="xl:sticky xl:top-[100px] self-start">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Preview</h3>
          {/* A tutors' form or an inquiry has one page to preview: no switch. */}
          {PREVIEW_PEOPLE[audience].length > 1 && (
            <div className="inline-flex rounded-full border border-[#98acfc] p-1">
              {PREVIEW_PEOPLE[audience].map((as) => (
                <button
                  key={as}
                  className={`px-3 py-1 rounded-full text-sm transition-colors duration-200 ${
                    shownAs === as
                      ? "bg-[#98acfc] text-white"
                      : "text-[#98acfc] hover:bg-[#98acfc]/10"
                  }`}
                  onClick={() => setPreviewAs(as)}
                >
                  {as === "parent"
                    ? "Parent"
                    : as === "student"
                    ? "Student"
                    : "Tutor"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-3 max-h-[78vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <PublicForm
            view={preview}
            preview
            paymentSlot={
              preview.payment ? (
                <PaymentPreview required={preview.payment.required} />
              ) : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}

export default function FormBuilder() {
  return <OrgAdminShell>{(orgId) => <Builder orgId={orgId} />}</OrgAdminShell>;
}
